import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { teamQueryKeys } from '@/lib/queryKeys';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { SomethingThatWorkedResponse } from '@/types/analytics';

/**
 * Period-based statistics (today, week, month)
 */
export interface PeriodStats {
  today: number;
  week: number;
  month: number;
}

/**
 * Dashboard Statistics Interface
 *
 * Matches the backend API response structure for dashboard stats
 */
export interface DashboardStats {
  readyToShoot: number;
  drafts: number;
  mappedAndReady: number;
  publishing: PeriodStats;
  mapped: PeriodStats;
  published: PeriodStats;
  canBeRemixed: number;
  bounced: number;
}

/**
 * API Response Interface
 */
interface DashboardStatsResponse {
  success: boolean;
  data: DashboardStats;
}

/**
 * Fetch dashboard statistics from API
 */
const fetchDashboardStats = async (period: string): Promise<DashboardStats> => {
  const { data } = await apiClient.get<DashboardStatsResponse>(`/api/dashboard/stats?period=${period}`);
  return data.data;
};

/**
 * Hook to fetch dashboard statistics
 *
 * Uses React Query to fetch and cache dashboard statistics with:
 * - Team-scoped query keys (auto-invalidates on team switch)
 * - Period-based filtering (today, week, month)
 * - 30 second stale time (data considered fresh for 30 seconds)
 * - 5 minute automatic refetch interval
 * - Only fetches when user has an active team
 *
 * @param period - Time period for stats filtering ('today' | 'week' | 'month')
 * @returns React Query result with dashboard stats
 *
 * @example
 * ```tsx
 * const { data: stats, isLoading } = useDashboardStats('week');
 *
 * if (isLoading) return <div>Loading...</div>;
 *
 * return <div>Ready to Shoot: {stats?.readyToShoot}</div>;
 * ```
 */
export const useDashboardStats = (period: 'today' | 'week' | 'month' = 'month') => {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useQuery({
    queryKey: [...teamQueryKeys.dashboard.stats(teamId), period],
    queryFn: () => fetchDashboardStats(period),
    enabled: hasTeam,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
  });
};

/**
 * Publishing Queue Item Interface
 *
 * Represents a post scheduled for publishing
 */
export interface PublishingQueueItem {
  id: number;
  mappingId: number;
  postId: number;
  variationId: number | null;
  caption: string | null;
  scheduledAt: string; // ISO string from API
  status: 'queued' | 'picked_up' | 'publishing';
  calendarName: string;
  channels: Array<{
    platform: string;
    platformUsername: string | null;
    profilePhotoUrl: string | null;
  }>;
  thumbnailUrl: string | null;
  mediaType: string | null;
  postMedia: Array<{
    id: number;
    thumbnailUrl: string | null;
    originalUrl: string;
    type: 'IMAGE' | 'VIDEO' | 'PDF';
    order: number;
  }>;
}

/**
 * Publishing Queue API Response
 */
interface PublishingQueueResponse {
  success: boolean;
  data: {
    items: PublishingQueueItem[];
    total: number;
  };
}

/**
 * Fetch publishing queue from API
 */
const fetchPublishingQueue = async (period: string): Promise<{ items: PublishingQueueItem[]; total: number }> => {
  const { data } = await apiClient.get<PublishingQueueResponse>(`/api/dashboard/publishing?period=${period}`);
  return data.data;
};

/**
 * Hook to fetch publishing queue items
 *
 * Uses React Query to fetch and cache scheduled posts with:
 * - Team-scoped query keys (auto-invalidates on team switch)
 * - Period-based filtering (today, week, month)
 * - 30 second stale time
 * - 5 minute automatic refetch interval
 * - Only fetches when user has an active team
 *
 * @param period - Time period for queue filtering ('today' | 'week' | 'month')
 * @returns React Query result with publishing queue items
 *
 * @example
 * ```tsx
 * const { data, isLoading } = usePublishingQueue('today');
 *
 * if (isLoading) return <div>Loading...</div>;
 *
 * return (
 *   <div>
 *     {data?.items.map(item => <PostCard key={item.id} post={item} />)}
 *   </div>
 * );
 * ```
 */
export const usePublishingQueue = (period: 'today' | 'week' | 'month' = 'today') => {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useQuery({
    queryKey: [...teamQueryKeys.dashboard.stats(teamId), 'publishing', period],
    queryFn: () => fetchPublishingQueue(period),
    enabled: hasTeam,
    staleTime: 30 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
};

/**
 * Mapped Queue Item Interface
 *
 * Represents a post mapped to calendar awaiting commit
 * Note: Channels not included - they are assigned during scheduling
 */
export interface MappedQueueItem {
  id: number;
  postId: number;
  variationId: number | null;
  caption: string | null;
  contentStatus: string;
  plannedDate: string; // ISO string from API
  calendarName: string;
  thumbnailUrl: string | null;
  mediaType: string | null;
  postMedia: Array<{
    id: number;
    thumbnailUrl: string | null;
    originalUrl: string;
    type: 'IMAGE' | 'VIDEO' | 'PDF';
    order: number;
  }>;
}

/**
 * Mapped Queue API Response
 */
interface MappedQueueResponse {
  success: boolean;
  data: {
    items: MappedQueueItem[];
    total: number;
  };
}

/**
 * Fetch mapped queue from API
 */
const fetchMappedQueue = async (period: string): Promise<{ items: MappedQueueItem[]; total: number }> => {
  const { data } = await apiClient.get<MappedQueueResponse>(`/api/dashboard/mapped?period=${period}`);
  return data.data;
};

/**
 * Hook to fetch mapped queue items
 *
 * Uses React Query to fetch and cache mapped posts with:
 * - Team-scoped query keys (auto-invalidates on team switch)
 * - Period-based filtering (today, week, month)
 * - 30 second stale time
 * - 5 minute automatic refetch interval
 * - Only fetches when user has an active team
 *
 * @param period - Time period for queue filtering ('today' | 'week' | 'month')
 * @returns React Query result with mapped queue items
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useMappedQueue('week');
 *
 * if (isLoading) return <div>Loading...</div>;
 *
 * return (
 *   <div>
 *     {data?.items.map(item => <PostCard key={item.id} post={item} />)}
 *   </div>
 * );
 * ```
 */
export const useMappedQueue = (period: 'today' | 'week' | 'month' = 'week') => {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useQuery({
    queryKey: [...teamQueryKeys.dashboard.stats(teamId), 'mapped', period],
    queryFn: () => fetchMappedQueue(period),
    enabled: hasTeam,
    staleTime: 30 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
};

/**
 * Published Queue Item Interface
 *
 * Represents a successfully published post
 */
export interface PublishedQueueItem {
  id: number;
  postId: number;
  variationId: number | null;
  caption: string | null;
  publishedAt: string; // ISO string from API
  calendarName: string;
  channels: Array<{
    platform: string;
    platformUsername: string | null;
    profilePhotoUrl: string | null;
    externalUrl: string | null;
  }>;
  thumbnailUrl: string | null;
  mediaType: string | null;
  postMedia: Array<{
    id: number;
    thumbnailUrl: string | null;
    originalUrl: string;
    type: 'IMAGE' | 'VIDEO' | 'PDF';
    order: number;
  }>;
}

/**
 * Remixable Queue Item Interface
 *
 * Represents a published post eligible for remixing (2+ weeks old)
 */
export interface RemixableQueueItem {
  id: number;
  postId: number;
  variationId: number | null;
  caption: string | null;
  publishedAt: string; // ISO string from API
  calendarName: string;
  channels: Array<{
    platform: string;
    platformUsername: string | null;
    profilePhotoUrl: string | null;
    externalUrl: string | null;
  }>;
  thumbnailUrl: string | null;
  mediaType: string | null;
  postMedia: Array<{
    id: number;
    thumbnailUrl: string | null;
    originalUrl: string;
    type: 'IMAGE' | 'VIDEO' | 'PDF';
    order: number;
  }>;
  totalReach: number;
  totalImpressions: number;
  totalEngagement: number;
  totalLikes: number;
  totalComments: number;
  totalShares: number;
  totalSaves: number;
  totalClicks: number;
  engagementRate: number;
  channelInstanceId: number | null;
}

/**
 * Published Queue API Response
 */
interface PublishedQueueResponse {
  success: boolean;
  data: {
    items: PublishedQueueItem[];
    total: number;
  };
}

/**
 * Remixable Queue API Response
 */
interface RemixableQueueResponse {
  success: boolean;
  data: {
    items: RemixableQueueItem[];
    total: number;
  };
}

/**
 * Fetch published queue from API
 */
const fetchPublishedQueue = async (period: string): Promise<{ items: PublishedQueueItem[]; total: number }> => {
  const { data } = await apiClient.get<PublishedQueueResponse>(`/api/dashboard/published?period=${period}`);
  return data.data;
};

/**
 * Fetch remixable queue from API
 */
const fetchRemixableQueue = async (): Promise<{ items: RemixableQueueItem[]; total: number }> => {
  const { data } = await apiClient.get<RemixableQueueResponse>('/api/dashboard/remix');
  return data.data;
};

/**
 * Hook to fetch published queue items
 *
 * Uses React Query to fetch and cache published posts with:
 * - Team-scoped query keys (auto-invalidates on team switch)
 * - Period-based filtering (today, week, month)
 * - 30 second stale time
 * - 5 minute automatic refetch interval
 * - Only fetches when user has an active team
 *
 * @param period - Time period for queue filtering ('today' | 'week' | 'month')
 * @returns React Query result with published queue items
 *
 * @example
 * ```tsx
 * const { data, isLoading } = usePublishedQueue('month');
 *
 * if (isLoading) return <div>Loading...</div>;
 *
 * return (
 *   <div>
 *     {data?.items.map(item => <PostCard key={item.id} post={item} />)}
 *   </div>
 * );
 * ```
 */
export const usePublishedQueue = (period: 'today' | 'week' | 'month' = 'month') => {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useQuery({
    queryKey: [...teamQueryKeys.dashboard.stats(teamId), 'published', period],
    queryFn: () => fetchPublishedQueue(period),
    enabled: hasTeam,
    staleTime: 30 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
};

/**
 * Hook to fetch remixable queue items
 *
 * Fetches published posts from 2+ weeks ago with engagement metrics,
 * sorted by engagement rate (best performers first)
 *
 * @returns React Query result with remixable queue items
 */
export const useRemixableQueue = () => {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useQuery({
    queryKey: [...teamQueryKeys.dashboard.stats(teamId), 'remix'],
    queryFn: fetchRemixableQueue,
    enabled: hasTeam,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Something That Worked Response
 */
interface SomethingThatWorkedApiResponse {
  success: boolean;
  data: SomethingThatWorkedResponse;
}

/**
 * Fetch "something that worked" top performer
 */
const fetchSomethingThatWorked = async (): Promise<SomethingThatWorkedResponse> => {
  const { data } = await apiClient.get<SomethingThatWorkedApiResponse>('/api/dashboard/something-worked');
  return data.data;
};

/**
 * Hook to fetch the top-performing posts for "Something That Worked" section
 *
 * Uses React Query to fetch and cache up to 5 top performers with:
 * - Team-scoped query keys (auto-invalidates on team switch)
 * - 5 minute stale time
 * - Only fetches when user has an active team
 *
 * @returns React Query result with top performers data
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useSomethingThatWorked();
 *
 * if (isLoading) return <div>Loading...</div>;
 *
 * if (!data?.topPerformers.length) return <div>No top performers found</div>;
 *
 * return <div>Top post: {data.topPerformers[0].postCaption}</div>;
 * ```
 */
export const useSomethingThatWorked = () => {
  const { teamId, hasTeam } = useEffectiveTeamId();
  return useQuery({
    queryKey: teamQueryKeys.dashboard.somethingWorked(teamId),
    queryFn: fetchSomethingThatWorked,
    enabled: hasTeam,
    staleTime: 5 * 60 * 1000,
  });
};

// ============================================================================
// Weekly Goals
// ============================================================================

/**
 * Goal Types
 */
export type GoalType = 'POSTS_PUBLISHED' | 'ENGAGEMENT_RATE' | 'TOTAL_REACH' | 'FOLLOWER_GROWTH';

/**
 * Goal Progress Item
 */
export interface GoalProgressItem {
  id: number;
  goalType: GoalType;
  targetValue: number;
  currentValue: number;
  isCompleted: boolean;
  label: string;
  unit: string;
  userId: number | null;
  platform: string | null;
  channelId: number | null;
  channelName: string | null;
}

/**
 * Weekly Goal Progress Response
 */
export interface WeeklyGoalProgressResponse {
  weekStart: string;
  weekEnd: string;
  channelId: number | null;
  userId: number | null;
  platform: string | null;
  goals: GoalProgressItem[];
  streak: number;
}

/**
 * Weekly Goal
 */
export interface WeeklyGoal {
  id: number;
  teamId: number;
  channelId: number | null;
  userId: number | null;
  platform: string | null;
  goalType: GoalType;
  targetValue: number;
  isActive: boolean;
  createdAt: string;
  createdBy: number;
  updatedAt: string;
  updatedBy: number | null;
}

/**
 * API Response Interfaces
 */
interface GoalProgressApiResponse {
  success: boolean;
  data: WeeklyGoalProgressResponse;
}

interface GoalsApiResponse {
  success: boolean;
  data: { goals: WeeklyGoal[] };
}

/**
 * Fetch goal progress for a channel or all channels
 */
const fetchGoalProgress = async (
  channelId?: number | null,
  userId?: number | null,
  platform?: string | null
): Promise<WeeklyGoalProgressResponse> => {
  const params = new URLSearchParams();
  if (channelId != null) params.set('channelId', String(channelId));
  if (userId != null) params.set('userId', String(userId));
  if (platform) params.set('platform', platform);
  const qs = params.toString();
  const { data } = await apiClient.get<GoalProgressApiResponse>(
    `/api/dashboard/goals/progress${qs ? `?${qs}` : ''}`
  );
  return data.data;
};

/**
 * Fetch all goals for the team
 */
const fetchAllGoals = async (): Promise<WeeklyGoal[]> => {
  const { data } = await apiClient.get<GoalsApiResponse>('/api/dashboard/goals');
  return data.data.goals;
};

/**
 * Hook to fetch weekly goal progress
 *
 * @param channelId - Optional channel ID to filter goals (null = all channels)
 * @param userId - Optional user ID to filter goals (null = team goals)
 * @param platform - Optional platform to filter goals (null = all platforms)
 * @returns React Query result with goal progress
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useWeeklyGoalProgress(channelId, userId, platform);
 *
 * if (isLoading) return <div>Loading...</div>;
 *
 * return (
 *   <div>
 *     {data?.goals.map(goal => (
 *       <ProgressBar key={goal.id} goal={goal} />
 *     ))}
 *   </div>
 * );
 * ```
 */
export const useWeeklyGoalProgress = (
  channelId?: number | null,
  userId?: number | null,
  platform?: string | null
) => {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useQuery({
    queryKey: [...teamQueryKeys.dashboard.goals(teamId), 'progress', channelId, userId, platform],
    queryFn: () => fetchGoalProgress(channelId, userId, platform),
    enabled: hasTeam,
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
  });
};

/**
 * Hook to fetch all weekly goals
 *
 * @returns React Query result with all team goals
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useWeeklyGoals();
 *
 * if (isLoading) return <div>Loading...</div>;
 *
 * return <div>Total goals: {data?.length}</div>;
 * ```
 */
export const useWeeklyGoals = () => {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useQuery({
    queryKey: [...teamQueryKeys.dashboard.goals(teamId), 'all'],
    queryFn: fetchAllGoals,
    enabled: hasTeam,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to create or update weekly goals
 *
 * @returns Mutation for upserting goals
 *
 * @example
 * ```tsx
 * const upsertMutation = useUpsertWeeklyGoals();
 *
 * const handleSave = () => {
 *   upsertMutation.mutate({
 *     channelId: 1,
 *     userId: null,
 *     platform: null,
 *     goals: [
 *       { goalType: 'POSTS_PUBLISHED', targetValue: 10 },
 *       { goalType: 'ENGAGEMENT_RATE', targetValue: 5.0 },
 *     ],
 *   });
 * };
 * ```
 */
export const useUpsertWeeklyGoals = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: async (payload: {
      channelId: number | null;
      userId: number | null;
      platform: string | null;
      goals: Array<{ goalType: GoalType; targetValue: number }>;
    }) => {
      const { data } = await apiClient.put<GoalsApiResponse>('/api/dashboard/goals', payload);
      return data.data.goals;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: teamQueryKeys.dashboard.goals(teamId),
      });
    },
  });
};

/**
 * Hook to delete a weekly goal
 *
 * @returns Mutation for deleting a goal
 *
 * @example
 * ```tsx
 * const deleteMutation = useDeleteWeeklyGoal();
 *
 * const handleDelete = (goalId: number) => {
 *   deleteMutation.mutate(goalId);
 * };
 * ```
 */
export const useDeleteWeeklyGoal = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: async (goalId: number) => {
      await apiClient.delete(`/api/dashboard/goals/${goalId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: teamQueryKeys.dashboard.goals(teamId),
      });
    },
  });
};
